import React from 'react';
import { Loader2, AlertCircle, FileText, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface InvoiceRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading: boolean;
}

const InvoiceRequestModal: React.FC<InvoiceRequestModalProps> = ({ isOpen, onClose, onConfirm, isLoading }) => {
    const { t } = useTranslation('billing');
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !m-0">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                <FileText size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900">{t('modals.invoice_request.title')}</h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-slate-600 transition p-1 hover:bg-slate-100 rounded-lg"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 mb-6">
                        <div className="flex gap-3">
                            <AlertCircle size={20} className="text-blue-600 shrink-0 mt-0.5" />
                            <div className="text-sm text-slate-600">
                                <p className="mb-2 font-medium text-slate-900">{t('modals.invoice_request.admin_notification')}</p>
                                <p>{t('modals.invoice_request.description')}</p>
                            </div>
                        </div>
                    </div>

                    <p className="text-slate-500 text-sm mb-6">
                        {t('modals.invoice_request.confirm_question')}
                    </p>

                    <div className="flex items-center gap-3 justify-end">
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-4 py-2 text-slate-600 font-bold text-sm hover:bg-slate-50 rounded-lg transition disabled:opacity-50"
                        >
                            {t('modals.invoice_request.cancel')}
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isLoading}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-lg flex items-center gap-2 transition disabled:opacity-50 shadow-lg shadow-blue-600/20"
                        >
                            {isLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                            {isLoading ? t('modals.invoice_request.requesting') : t('modals.invoice_request.confirm')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceRequestModal;
